import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { proposalsApi } from '../services/api';
import type { Proposal } from '../types';
import { Avatar } from '../components/ui/Avatar';
import { Button } from '../components/ui/Button';
import { MaterialIcon } from '../components/ui/MaterialIcon';
import { ProposalStatusChip } from '../components/obros/ProposalTile';
import { formatCurrency, formatDate } from '../utils/helpers';
import { useAuthStore } from '../stores/authStore';

// Calca app/obros/lib/screens/proposal_detail_screen.dart
export const ProposalDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [proposal, setProposal] = useState<Proposal | null>(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);

  const load = () => proposalsApi.get(Number(id)).then(({ data }) => setProposal(data));

  useEffect(() => {
    load().finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loading || !proposal) return null;

  const isJobOwner = user?.id === proposal.job?.client_id;
  const otherPerson = isJobOwner ? proposal.worker : proposal.job?.client;
  const canAct = isJobOwner && proposal.status === 'pending';

  const handleAccept = async () => {
    if (!window.confirm('¿Aceptar esta propuesta? El trabajo pasará a estar en progreso.')) return;
    setActing(true);
    try {
      await proposalsApi.accept(proposal.id);
      await load();
    } finally {
      setActing(false);
    }
  };

  const handleReject = async () => {
    if (!window.confirm('¿Rechazar esta propuesta?')) return;
    setActing(true);
    try {
      await proposalsApi.reject(proposal.id);
      await load();
    } finally {
      setActing(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 pb-28">
      <button
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-1 text-sm font-medium text-onSurface-mute hover:text-onSurface mb-4"
      >
        <MaterialIcon name="arrow_back" size={16} /> Volver
      </button>

      {otherPerson && (
        <div className="bg-surface border border-outlineVarWarm rounded-[20px] p-5 flex items-center gap-3 mb-4">
          <Avatar src={otherPerson.avatar} name={otherPerson.full_name} size="lg" />
          <div>
            <p className="font-semibold text-onSurface">{otherPerson.full_name}</p>
            <div className="flex items-center gap-1 text-sm text-onSurface-mute">
              <MaterialIcon name="star" size={14} fill className="text-warning-500" />
              {(Number(otherPerson.rating_average) || 0).toFixed(1)}
            </div>
            {otherPerson.bio && <p className="text-sm text-onSurface-var mt-1 line-clamp-2">{otherPerson.bio}</p>}
          </div>
        </div>
      )}

      <div className="bg-surface border border-outlineVarWarm rounded-[20px] p-5 mb-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold text-onSurface">{proposal.job?.title}</h2>
          <ProposalStatusChip status={proposal.status} />
        </div>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <p className="text-onSurface-mute">Monto</p>
            <p className="font-semibold text-primary-700">{formatCurrency(proposal.amount)}</p>
          </div>
          {proposal.estimated_days && (
            <div>
              <p className="text-onSurface-mute">Plazo estimado</p>
              <p className="font-semibold text-onSurface">{proposal.estimated_days} días</p>
            </div>
          )}
          <div>
            <p className="text-onSurface-mute">Enviada</p>
            <p className="font-semibold text-onSurface">{formatDate(proposal.created_at)}</p>
          </div>
        </div>
      </div>

      {proposal.cover_letter && (
        <div className="bg-surface border border-outlineVarWarm rounded-[20px] p-5">
          <h3 className="text-sm font-semibold text-onSurface mb-2">Carta de presentación</h3>
          <p className="text-sm text-onSurface-var whitespace-pre-wrap">{proposal.cover_letter}</p>
        </div>
      )}

      <div className="fixed bottom-0 inset-x-0 bg-surface border-t border-outlineVarWarm px-4 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] flex gap-2 lg:left-60">
        {canAct && (
          <>
            <Button variant="outlined" pill className="flex-1" onClick={handleReject} isLoading={acting}>
              Rechazar
            </Button>
            <Button variant="filled" pill className="flex-1" onClick={handleAccept} isLoading={acting}>
              Aceptar
            </Button>
          </>
        )}
        {otherPerson && (
          <Link to={`/messages/new?recipientId=${otherPerson.id}&jobId=${proposal.job_id}`} className={canAct ? 'shrink-0' : 'flex-1'}>
            <Button variant={canAct ? 'tonal' : 'filled'} pill className="w-full justify-center">
              <MaterialIcon name="chat_bubble" size={16} /> Chatear con {otherPerson.first_name}
            </Button>
          </Link>
        )}
      </div>
    </div>
  );
};
